




import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import vue from 'vue';
import { colors } from '@/config';
import { highchartsSpacing } from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class CustomCircleChart extends Vue {
  @Prop() subSlide!: any;

  @Prop() dark!: boolean;
  @Prop() containerWidth: any;
  @Prop() currentLanguage: any;

  chartOptions: any = {};
  centerTextElement: any = null;
  centerTextGroup: any = null;

  // Array med tolv färger för cirkeldiagram
  pieColors: string[] = [
    colors.PrimaryLight,
    colors.Secondary,
    colors.Tertiary,
    '#FF5149', // Korallröd
    '#CE2C2C',
    '#CCC4BD',
    '#D6D0CA',
    '#E0DCD6',
    '#EBE7E5',
    '#F5F3F2',
    '#F2F0EE',
    '#EDEAE8',
  ];

  update() {
    this.$nextTick(() => {
      const chart = this.$refs.chart as any;
      if (chart && chart.chart) {
        chart.chart.reflow();
      }
    });

    this.chartOptions = {
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        ...highchartsSpacing(this.containerWidth),
        events: {
          load: () => {
            this.addCenterText();
          },
          redraw: () => {
            this.addCenterText();
          },
        },
      },

      credits: {
        enabled: false,
      },

      colors: this.pieColors,

      title: {
        text: this.subSlide.diagramTitle || '',
        style: {
          fontSize: '2cqw',
          color: colors.Light,
        },
      },

      tooltip: {
        enabled: false,
      },

      legend: {
        verticalAlign: 'bottom',
        align: 'center',
        enabled: false,
        itemStyle: {
          fontSize: '1cqw',
          fontWeight: 'normal',
          color: colors.Light,
        },
        itemHoverStyle: {
          color: colors.Light,
        },
      },

      plotOptions: {
        pie: {
          allowPointSelect: false,
          cursor: 'pointer',
          dataLabels: {
            enabled: true,
            format: '{point.name}',
            style: {
              fontSize: '1cqw',
              color: colors.Light,
            },
            distance: this.transformPx(20),
            connectorWidth: 0,
            allowOverlap: true,
          },
          showInLegend: true,
          point: {
            events: {
              mouseOver: (e: any) => {
                this.updateCenterText(e.target.name, e.target.y);
              },
              mouseOut: () => {
                this.updateCenterText('Totalt', this.getTotalValue());
              },
            },
          },
        },
      },

      series: [
        {
          name: this.subSlide.series1 || '',
          innerSize: '50%',
          data: this.getData(),
          borderColor: colors.Dark,
          borderWidth: this.transformPx(1),
        },
      ],
    };
  }

  mounted() {
    this.update();
  }

  @Watch('subSlide', { immediate: true, deep: true })
  subSlideChanged() {
    this.update();
    this.$nextTick(() => {
      this.addCenterText();
    });
  }

  @Watch('containerWidth', { immediate: false, deep: true })
  containerWidthChanged() {
    this.update();
    this.$nextTick(() => {
      this.addCenterText();
    });
  }

  getData(): any[] {
    if (!this.subSlide || !this.subSlide.categories || !this.subSlide.values1) {
      return [];
    }

    const data: any[] = [];
    let colorIndex = 0;
    for (let i = 0; i < 12; i++) {
      const category = this.subSlide.categories[i];
      const value = this.subSlide.values1[i];

      if (
        category &&
        category.trim() !== '' &&
        value !== null &&
        value !== undefined &&
        value !== ''
      ) {
        data.push({
          name: category,
          y: parseFloat(value),
          color: this.pieColors[colorIndex % this.pieColors.length],
        });
        colorIndex++;
      }
    }

    return data;
  }

  transformPx(value: any) {
    if (value * (this.containerWidth / 1920) < 1) {
      return 1;
    }
    return value * (this.containerWidth / 1920);
  }

  getTotalValue(): number {
    const data = this.getData();
    return data.reduce((sum, point) => sum + point.y, 0);
  }

  addCenterText() {
    const totalValue = this.getTotalValue();
    this.updateCenterText('Totalt', totalValue);
  }

  updateCenterText(label: string, value: number) {
    const chartComponent = this.$refs.chart as any;
    if (!chartComponent || !chartComponent.chart) {
      return;
    }

    const chart = chartComponent.chart;

    // Ta bort tidigare text om den finns
    if (this.centerTextGroup) {
      this.centerTextGroup.destroy();
      this.centerTextGroup = null;
    }

    // Beräkna centrum
    const centerX = chart.plotWidth / 2 + chart.plotLeft;
    const centerY = chart.plotHeight / 2 + chart.plotTop;
    const fontSize1 = this.transformPx(24); // 2cqw motsvarar ungefär 24px vid 1920px bredd
    const fontSize2 = this.transformPx(30); // 1.5cqw motsvarar ungefär 18px vid 1920px bredd
    const margin = this.transformPx(8); // Liten marginal mellan raderna

    // Skapa en grupp för texten
    this.centerTextGroup = chart.renderer
      .g()
      .attr({ zIndex: 10 })
      .add();

    // Första raden: label (t.ex. "Totalt" eller datapointens namn)
    const text1 = chart.renderer
      .text(label, centerX, centerY - fontSize2 / 2 - margin / 2)
      .attr({
        align: 'center',
      })
      .css({
        color: colors.Light,
        fontSize: fontSize1 + 'px',
      })
      .add(this.centerTextGroup);

    // Andra raden: värdet
    const text2 = chart.renderer
      .text(
        // (vue as any).options.filters.P(value),
        value + ' ' + this.subSlide.diagramUnit,
        centerX,
        centerY + fontSize1 / 2 + margin / 2,
      )
      .attr({
        align: 'center',
      })
      .css({
        color: colors.Light,
        fontSize: fontSize2 + 'px',
        fontWeight: 'bold',
      })
      .add(this.centerTextGroup);
  }
}
